import type { TAnalyticsMyAnalyticsBE, TError } from '@onesource/schemas';
import { type QueryClient, useQuery } from '@tanstack/react-query';

import axios from '../../axios';
import { ENTITY } from '../entity';

const MY_ANALYTICS_KEY = [ENTITY, 'my-analytics'] as const;

/**
 * Tracking-backed conditions are mirrored to the tracking service over RabbitMQ,
 * so the count can lag a completion by a moment. A second invalidation after this
 * delay catches the row that the immediate refetch was too early for.
 */
const TRACKING_LAG_MS = 2500;

export const getMyAnalyticsQueryKey = (spaceId: string) => [...MY_ANALYTICS_KEY, spaceId];

/**
 * The current user's status against every analytics rule that applies to them
 * in a program — evaluated live by the API on each request.
 *
 * @param spaceId - the "My Analytics" sub-space id (or the program id)
 */
export const useGetMyAnalytics = (spaceId?: string) => {
  return useQuery<TAnalyticsMyAnalyticsBE, TError>({
    queryKey: getMyAnalyticsQueryKey(spaceId ?? ''),
    queryFn: async () => {
      const res = await axios.get<TAnalyticsMyAnalyticsBE>(`/community-hub/spaces/${spaceId}/my-analytics`);
      return res.data;
    },
    enabled: !!spaceId,
    staleTime: 30 * 1000,
    refetchOnWindowFocus: true,
  });
};

/**
 * Refresh every mounted My Analytics query — now, and once more after the
 * tracking mirror has had time to land. Call from any mutation that can
 * satisfy a requirement (block/lesson completion, form/quiz submit, post/comment).
 */
export const invalidateMyAnalytics = (queryClient: QueryClient): void => {
  queryClient.invalidateQueries({ queryKey: MY_ANALYTICS_KEY });
  setTimeout(() => queryClient.invalidateQueries({ queryKey: MY_ANALYTICS_KEY }), TRACKING_LAG_MS);
};
